import { ConfigManager } from '../config/manager';
import type { OutputFormat } from '../utils/output';
import type { BaserowConfig, ProjectConfig } from '../config/types';

/** @typedef {import('../utils/output').OutputFormat} OutputFormat */

/**
 * @typedef {Object} ConfigSetOptions
 * @property {boolean} [global]
 * @property {OutputFormat} [format]
 */

export interface ConfigSetOptions {
  global?: boolean;
  format?: OutputFormat;
}

/**
 * Sets a configuration value.
 * @param key The configuration key
 * @param value The value to set
 * @param options Configuration options
 * @returns A message indicating the result
 */
export async function configSet(key: string, value: string, options: ConfigSetOptions = {}): Promise<string> {
  const config = ConfigManager.getInstance();
  if (options.global) {
    const globalConfig = config.getGlobalConfig();
    let current: any = globalConfig;
    const keys = key.split('.');
    if (keys.length === 0) {
      throw new Error('Invalid key provided');
    }
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];
      if (k && !(k in current)) {
        current[k] = {};
      }
      if (k) {
        current = current[k];
      }
    }
    const lastKey = keys[keys.length - 1];
    if (lastKey) {
      current[lastKey] = value;
    }
    config.saveGlobalConfig(globalConfig);
    return `Set global config ${key}=${value}`;
  } else {
    const localConfig = config.getLocalConfig() || {
      name: process.cwd().split('/').pop() || 'baserow-project'
    };
    const validConfigKeys = [
      'url', 'token', 'tokenType', 'refreshToken', 'database', 
      'output', 'tables', 'userEmail', 'userFirstName'
    ];
    if (key in localConfig || validConfigKeys.includes(key)) {
      (localConfig as any)[key] = value;
    } else {
      throw new Error(`Invalid local config key: ${key}`);
    }
    config.saveLocalConfig(localConfig);
    return `Set local config ${key}=${value}`;
  }
}

/**
 * Gets a configuration value or the full config object.
 * @param key The configuration key (optional)
 * @param options Configuration options
 * @returns The config value, or the full config object if no key is provided
 */
export async function configGet(key?: string, options: ConfigSetOptions = {}): Promise<any> {
  const config = ConfigManager.getInstance();
  const currentConfig = options.global ? 
    config.getGlobalConfig() : 
    (config.getLocalConfig() || { name: process.cwd().split('/').pop() || 'baserow-project' });
  console.log('Current config:', currentConfig);
  if (!key) {
    return currentConfig;
  }
  const keys = key.split('.');
  let value: any = currentConfig;
  for (const k of keys) {
    if (value && typeof value === 'object' && k) {
      value = value[k];
    } else {
      value = undefined;
      break;
    }
  }
  if (value === undefined) {
    throw new Error(`Configuration key "${key}" not found`);
  }
  return value;
}

/**
 * Initializes a new project configuration.
 * @param name Optional project name
 * @returns A message indicating the result
 */
export async function configInit(name?: string): Promise<string> {
  const config = ConfigManager.getInstance();
  await config.initializeProject(name);
  return 'Initialized project configuration';
} 